import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Plus,
  Edit2,
  Trash2,
  GripVertical,
  PlayCircle,
  FileText,
  ChevronDown,
  ChevronRight,
  Save,
  Loader2,
  ArrowLeft,
  BookOpen,
  HelpCircle,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../lib/translations';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CourseContentManager = () => {
  const { courseId } = useParams();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [expandedChapters, setExpandedChapters] = useState({});
  
  // Dialog states
  const [chapterDialog, setChapterDialog] = useState({ open: false, data: null });
  const [lessonDialog, setLessonDialog] = useState({ open: false, data: null, chapterId: null });
  const [quizDialog, setQuizDialog] = useState({ open: false, data: null, chapterId: null });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCourseContent();
  }, [courseId]);

  const fetchCourseContent = async () => {
    try {
      setLoading(true);
      
      const courseRes = await axios.get(`${API_URL}/courses/${courseId}`);
      setCourse(courseRes.data);
      
      const chaptersRes = await axios.get(`${API_URL}/elearning/courses/${courseId}/chapters`);
      
      // Fetch lessons and quiz for each chapter
      const chaptersWithContent = await Promise.all(
        chaptersRes.data.map(async (chapter) => {
          const [lessonsRes, quizRes] = await Promise.all([
            axios.get(`${API_URL}/elearning/chapters/${chapter.id}/lessons`),
            axios.get(`${API_URL}/elearning/chapters/${chapter.id}/quiz`).catch(() => ({ data: null }))
          ]);
          return {
            ...chapter,
            lessons: lessonsRes.data,
            quiz: quizRes.data
          };
        })
      );
      
      setChapters(chaptersWithContent);
      
      // Expand all chapters by default
      const expanded = {};
      chaptersWithContent.forEach(ch => expanded[ch.id] = true);
      setExpandedChapters(expanded);
      
    } catch (error) {
      console.error('Error fetching course content:', error);
    } finally {
      setLoading(false);
    }
  };

  // Chapter CRUD
  const saveChapter = async (data) => {
    try {
      setSaving(true);
      
      if (data.id) {
        await axios.patch(`${API_URL}/elearning/chapters/${data.id}`, {
          title: data.title,
          description: data.description
        });
      } else {
        await axios.post(`${API_URL}/elearning/chapters`, {
          course_id: courseId,
          title: data.title,
          description: data.description
        });
      }
      
      await fetchCourseContent();
      setChapterDialog({ open: false, data: null });
      
    } catch (error) {
      console.error('Error saving chapter:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const deleteChapter = async (chapterId) => {
    if (!confirm('Supprimer ce chapitre et toutes ses leçons ?')) return;
    
    try {
      await axios.delete(`${API_URL}/elearning/chapters/${chapterId}`);
      await fetchCourseContent();
    } catch (error) {
      console.error('Error deleting chapter:', error);
    }
  };

  // Lesson CRUD
  const saveLesson = async (data) => {
    try {
      setSaving(true);
      
      if (data.id) {
        await axios.patch(`${API_URL}/elearning/lessons/${data.id}`, data);
      } else {
        await axios.post(`${API_URL}/elearning/lessons`, {
          ...data,
          chapter_id: lessonDialog.chapterId
        });
      }
      
      await fetchCourseContent();
      setLessonDialog({ open: false, data: null, chapterId: null });
      
    } catch (error) {
      console.error('Error saving lesson:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const deleteLesson = async (lessonId) => {
    if (!confirm('Supprimer cette leçon ?')) return;
    
    try {
      await axios.delete(`${API_URL}/elearning/lessons/${lessonId}`);
      await fetchCourseContent();
    } catch (error) {
      console.error('Error deleting lesson:', error);
    }
  };

  // Quiz CRUD
  const saveQuiz = async (data) => {
    try {
      setSaving(true);
      
      if (data.id) {
        await axios.patch(`${API_URL}/elearning/quizzes/${data.id}`, data);
      } else {
        await axios.post(`${API_URL}/elearning/quizzes`, {
          ...data,
          chapter_id: quizDialog.chapterId
        });
      }
      
      await fetchCourseContent();
      setQuizDialog({ open: false, data: null, chapterId: null });
      
    } catch (error) {
      console.error('Error saving quiz:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const toggleChapter = (chapterId) => {
    setExpandedChapters(prev => ({
      ...prev,
      [chapterId]: !prev[chapterId]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2">Chargement du contenu E-Learning...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="course-content-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-gray-900">
              Gestion du contenu
            </h1>
            <p className="text-gray-500">{course?.name}</p>
          </div>
        </div>
        
        <Button onClick={() => setChapterDialog({ open: true, data: null })}>
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un chapitre
        </Button>
      </div>

      {/* Chapters */}
      <div className="space-y-4">
        {chapters.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-heading font-bold text-gray-900 mb-2">
                Aucun contenu
              </h3>
              <p className="text-gray-500 mb-4">
                Commencez par ajouter un chapitre à ce cours
              </p>
              <Button onClick={() => setChapterDialog({ open: true, data: null })}>
                <Plus className="w-4 h-4 mr-2" />
                Créer le premier chapitre
              </Button>
            </CardContent>
          </Card>
        ) : (
          chapters.map((chapter, chapterIndex) => (
            <Card key={chapter.id} className="border-0 shadow-sm overflow-hidden">
              {/* Chapter Header */}
              <div 
                className="flex items-center gap-3 p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => toggleChapter(chapter.id)}
              >
                <GripVertical className="w-5 h-5 text-gray-400" />
                <span className="w-8 h-8 bg-secondary text-white rounded-full flex items-center justify-center text-sm font-bold">
                  {chapterIndex + 1}
                </span>
                
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-gray-900">{chapter.title}</h3>
                  <p className="text-sm text-gray-500">
                    {chapter.lessons_count} leçons • {chapter.total_duration_minutes} min
                  </p>
                </div>
                
                <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setChapterDialog({ open: true, data: chapter })}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteChapter(chapter.id)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  {expandedChapters[chapter.id] ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
              
              {/* Chapter Content */}
              {expandedChapters[chapter.id] && (
                <CardContent className="p-4 pt-0">
                  {/* Lessons */}
                  <div className="space-y-2 mt-4">
                    {chapter.lessons?.map((lesson, lessonIndex) => (
                      <div
                        key={lesson.id}
                        className="flex items-center gap-3 p-3 bg-white border rounded-lg hover:shadow-sm transition-shadow"
                      >
                        <PlayCircle className="w-5 h-5 text-primary" />
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {lessonIndex + 1}. {lesson.title}
                          </p>
                          <p className="text-xs text-gray-500">
                            {lesson.video_type !== 'none' && `${lesson.video_duration_minutes} min`}
                            {lesson.video_type && ` • ${lesson.video_type}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setLessonDialog({ 
                              open: true, 
                              data: lesson, 
                              chapterId: chapter.id 
                            })}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteLesson(lesson.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-2 border-dashed"
                      onClick={() => setLessonDialog({ 
                        open: true, 
                        data: null, 
                        chapterId: chapter.id 
                      })}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter une leçon
                    </Button>
                  </div>
                  
                  {/* Quiz */}
                  <div className="mt-4 pt-4 border-t">
                    {chapter.quiz ? (
                      <div className="flex items-center gap-3 p-3 bg-accent/10 rounded-lg">
                        <HelpCircle className="w-5 h-5 text-accent" />
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{chapter.quiz.title}</p>
                          <p className="text-xs text-gray-500">
                            {chapter.quiz.questions_count} questions • 
                            Score minimum: {chapter.quiz.passing_score}%
                            {chapter.quiz.is_published ? ' • Publié' : ' • Brouillon'}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/admin/quizzes/${chapter.quiz.id}/questions`)}
                          >
                            <FileText className="w-4 h-4 mr-1" />
                            Questions
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setQuizDialog({ 
                              open: true, 
                              data: chapter.quiz, 
                              chapterId: chapter.id 
                            })}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full border-dashed"
                        onClick={() => setQuizDialog({ 
                          open: true, 
                          data: null, 
                          chapterId: chapter.id 
                        })}
                      >
                        <HelpCircle className="w-4 h-4 mr-2" />
                        Ajouter un quiz
                      </Button>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          ))
        )}
      </div>

      {/* Chapter Dialog */}
      <Dialog open={chapterDialog.open} onOpenChange={(open) => !open && setChapterDialog({ open: false, data: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {chapterDialog.data ? 'Modifier le chapitre' : 'Nouveau chapitre'}
            </DialogTitle>
          </DialogHeader>
          <ChapterForm
            data={chapterDialog.data}
            onSave={saveChapter}
            saving={saving}
          />
        </DialogContent>
      </Dialog>

      {/* Lesson Dialog */}
      <Dialog open={lessonDialog.open} onOpenChange={(open) => !open && setLessonDialog({ open: false, data: null, chapterId: null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {lessonDialog.data ? 'Modifier la leçon' : 'Nouvelle leçon'}
            </DialogTitle>
          </DialogHeader>
          <LessonForm
            data={lessonDialog.data}
            onSave={saveLesson}
            saving={saving}
          />
        </DialogContent>
      </Dialog>

      {/* Quiz Dialog */}
      <Dialog open={quizDialog.open} onOpenChange={(open) => !open && setQuizDialog({ open: false, data: null, chapterId: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {quizDialog.data ? 'Modifier le quiz' : 'Nouveau quiz'}
            </DialogTitle>
          </DialogHeader>
          <QuizForm
            data={quizDialog.data}
            onSave={saveQuiz}
            saving={saving}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Chapter Form Component
const ChapterForm = ({ data, onSave, saving }) => {
  const [form, setForm] = useState({
    id: data?.id || null,
    title: data?.title || '',
    description: data?.description || ''
  });

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="title">Titre du chapitre *</Label>
        <Input
          id="title"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="Ex: Introduction à l'allemand"
        />
      </div>
      <div>
        <Label htmlFor="description">{t('form_description', lang)}</Label>
        <Textarea
          id="description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Description du chapitre..."
          rows={3}
        />
      </div>
      <DialogFooter>
        <Button onClick={() => onSave(form)} disabled={saving || !form.title}>
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          <Save className="w-4 h-4 mr-2" />
          Enregistrer
        </Button>
      </DialogFooter>
    </div>
  );
};

// Lesson Form Component
const LessonForm = ({ data, onSave, saving }) => {
  const [form, setForm] = useState({
    id: data?.id || null,
    title: data?.title || '',
    description: data?.description || '',
    content: data?.content || '',
    video_url: data?.video_url || '',
    video_type: data?.video_type || 'none',
    video_duration_minutes: data?.video_duration_minutes || 0,
    is_free_preview: data?.is_free_preview || false
  });
  const [uploadingMedia, setUploadingMedia] = useState(false);

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="lesson-title">Titre de la leçon *</Label>
        <Input
          id="lesson-title"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="Ex: Les salutations en allemand"
        />
      </div>
      
      <div>
        <Label htmlFor="lesson-description">{t('form_description', lang)}</Label>
        <Textarea
          id="lesson-description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Courte description de la leçon..."
          rows={2}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="video-type">Type de contenu média</Label>
          <Select
            value={form.video_type}
            onValueChange={(value) => setForm({ ...form, video_type: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Pas de média</SelectItem>
              <SelectItem value="youtube">YouTube</SelectItem>
              <SelectItem value="vimeo">Vimeo</SelectItem>
              <SelectItem value="upload">Upload fichier (vidéo/audio/PDF)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label htmlFor="duration">Durée (minutes)</Label>
          <Input
            id="duration"
            type="number"
            value={form.video_duration_minutes}
            onChange={(e) => setForm({ ...form, video_duration_minutes: parseInt(e.target.value) || 0 })}
            min={0}
          />
        </div>
      </div>

      {form.video_type !== 'none' && (
        <div>
          <Label htmlFor="video-url">{form.video_type === 'upload' ? 'Fichier média' : 'URL'}</Label>
          {form.video_type === 'upload' ? (
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input
                  id="video-url"
                  value={form.video_url}
                  onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                  placeholder="URL du fichier (auto-remplie après upload)"
                  readOnly={uploadingMedia}
                  className="flex-1"
                />
                <label className="cursor-pointer">
                  <input
                    type="file"
                    className="hidden"
                    accept=".mp4,.webm,.mp3,.wav,.ogg,.pdf"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      e.target.value = '';
                      setUploadingMedia(true);
                      try {
                        const fd = new FormData();
                        fd.append('file', file);
                        const res = await axios.post(`${API_URL}/elearning/upload-media`, fd);
                        setForm(prev => ({ ...prev, video_url: res.data.url, video_duration_minutes: prev.video_duration_minutes || 0 }));
                        toast.success(`"${file.name}" uploadé`);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Erreur upload');
                      } finally {
                        setUploadingMedia(false);
                      }
                    }}
                  />
                  <span className="inline-flex items-center justify-center h-10 px-4 rounded-md border hover:bg-muted cursor-pointer text-sm font-medium">
                    {uploadingMedia ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Upload'}
                  </span>
                </label>
              </div>
              {form.video_url && form.video_url.startsWith('/api/') && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Fichier uploadé
                </p>
              )}
              <p className="text-xs text-muted-foreground">Formats : MP4, WebM, MP3, WAV, OGG, PDF (max 200MB)</p>
            </div>
          ) : (
            <Input
              id="video-url"
              value={form.video_url}
              onChange={(e) => setForm({ ...form, video_url: e.target.value })}
              placeholder={
                form.video_type === 'youtube' 
                  ? 'https://www.youtube.com/watch?v=...' 
                  : 'https://vimeo.com/...'
              }
            />
          )}
        </div>
      )}

      <div>
        <Label htmlFor="content">Contenu textuel (optionnel)</Label>
        <Textarea
          id="content"
          value={form.content}
          onChange={(e) => setForm({ ...form, content: e.target.value })}
          placeholder="Contenu de la leçon (supporte le HTML)..."
          rows={5}
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="is-free"
          checked={form.is_free_preview}
          onChange={(e) => setForm({ ...form, is_free_preview: e.target.checked })}
          className="rounded"
        />
        <Label htmlFor="is-free" className="cursor-pointer">
          Aperçu gratuit (accessible sans inscription)
        </Label>
      </div>

      <DialogFooter>
        <Button onClick={() => onSave(form)} disabled={saving || !form.title}>
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          <Save className="w-4 h-4 mr-2" />
          Enregistrer
        </Button>
      </DialogFooter>
    </div>
  );
};

// Quiz Form Component
const QuizForm = ({ data, onSave, saving }) => {
  const [form, setForm] = useState({
    id: data?.id || null,
    title: data?.title || '',
    description: data?.description || '',
    passing_score: data?.passing_score || 70,
    time_limit_minutes: data?.time_limit_minutes || null,
    max_attempts: data?.max_attempts || 3
  });

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="quiz-title">Titre du quiz *</Label>
        <Input
          id="quiz-title"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="Ex: Quiz - Chapitre 1"
        />
      </div>
      
      <div>
        <Label htmlFor="quiz-description">{t('form_description', lang)}</Label>
        <Textarea
          id="quiz-description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Instructions pour le quiz..."
          rows={2}
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label htmlFor="passing-score">Score minimum (%)</Label>
          <Input
            id="passing-score"
            type="number"
            value={form.passing_score}
            onChange={(e) => setForm({ ...form, passing_score: parseInt(e.target.value) || 70 })}
            min={0}
            max={100}
          />
        </div>
        
        <div>
          <Label htmlFor="time-limit">Limite de temps (min)</Label>
          <Input
            id="time-limit"
            type="number"
            value={form.time_limit_minutes || ''}
            onChange={(e) => setForm({ ...form, time_limit_minutes: parseInt(e.target.value) || null })}
            min={0}
            placeholder="Illimité"
          />
        </div>

        <div>
          <Label htmlFor="max-attempts">Tentatives max</Label>
          <Input
            id="max-attempts"
            type="number"
            value={form.max_attempts}
            onChange={(e) => setForm({ ...form, max_attempts: parseInt(e.target.value) || 1 })}
            min={1}
          />
        </div>
      </div>

      <DialogFooter>
        <Button onClick={() => onSave(form)} disabled={saving || !form.title}>
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          <Save className="w-4 h-4 mr-2" />
          Enregistrer
        </Button>
      </DialogFooter>
    </div>
  );
};

export default CourseContentManager;
